import { useEffect } from "react";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useNavigate } from "react-router-dom";
import { Briefcase, MapPin, Building2, Filter, Loader2, Bookmark } from "lucide-react";
import { usePublishedJobs } from "@/hooks/useJobPosts";
import { useMyApplications } from "@/hooks/useJobApplications";
import { useStudentSkills } from "@/hooks/useStudentSkills";
import { calculateMatchScore } from "@/lib/skills";
import { MatchBadge } from "@/components/MatchBadge";
import { markVagasVisited } from "@/hooks/useVagasNotifications";
import { getCompanyName, getCompanyLogoUrl, isCrawlerJob, isExternalJob, getSourceLabel, getSourceName } from "@/lib/jobUtils";
import { WorkModelBadge } from "@/components/jobs/WorkModelBadge";
import { DeadlineBadge } from "@/components/jobs/DeadlineBadge";

export default function AlunoVagas() {
  const navigate = useNavigate();
  const { data: jobs, isLoading } = usePublishedJobs();
  const { skills: mySkills } = useStudentSkills();
  const { applications } = useMyApplications();

  useEffect(() => {
    markVagasVisited();
  }, []);

  if (isLoading) {
    return (
      <div className="page-container flex items-center justify-center min-h-[50vh]">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  const vagas = (jobs ?? []).map(j => {
    const reqSkills = Array.isArray(j.required_skills) ? (j.required_skills as string[]) : [];
    const niceSkills = Array.isArray(j.nice_skills) ? (j.nice_skills as string[]) : [];
    const match = calculateMatchScore(mySkills, reqSkills, niceSkills);
    const logoUrl = getCompanyLogoUrl(j);
    const skillsTags: string[] = Array.isArray(j.skills_tags) ? j.skills_tags : [];
    return {
      id: j.id,
      titulo: j.title,
      empresa: getCompanyName(j),
      logoUrl,
      tipo: j.employment_type,
      tipoLabel: j.employment_type,
      work_model: j.work_model,
      local: j.work_model === "remote" ? null : [j.location_city, j.location_state].filter(Boolean).join(", "),
      deadline_date: j.deadline_date ?? null,
      description_summary: j.description_summary ?? null,
      skillsTags,
      matchPercentage: match.percentage,
      hasSkillData: reqSkills.length > 0 || niceSkills.length > 0,
      isCrawler: isCrawlerJob(j),
      isExternal: isExternalJob(j),
      sourceName: getSourceName(j),
    };
  });

  return (
    <div className="page-container">
      <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-heading text-2xl font-bold text-foreground">Vagas</h1>
          <p className="text-muted-foreground text-sm mt-1">Oportunidades de entrada</p>
        </div>
        <Button variant="outline" size="sm"><Filter className="w-4 h-4" />Filtrar</Button>
      </motion.div>

      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
        className="bg-success/10 rounded-xl p-4 mb-6 border border-success/20">
        <p className="text-sm text-foreground font-medium">🎯 Vagas de entrada — sem exigir experiência</p>
        <p className="text-xs text-muted-foreground mt-1">Todas as vagas aqui são para quem está começando</p>
      </motion.div>

      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }} className="mb-6 space-y-2">
        <Button variant="secondary" className="w-full justify-between" onClick={() => navigate("/aluno/vagas/candidaturas")}>
          <span className="flex items-center gap-2"><Briefcase className="w-4 h-4" />Minhas Candidaturas</span>
          <Badge variant="default" className="text-[10px]">{applications.length}</Badge>
        </Button>
        <Button variant="secondary" className="w-full justify-between" onClick={() => navigate("/aluno/vagas/salvas")}>
          <span className="flex items-center gap-2"><Bookmark className="w-4 h-4" />Vagas Salvas</span>
        </Button>
      </motion.div>

      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="space-y-3">
        {vagas.map((vaga) => (
          <div key={vaga.id} onClick={() => navigate(`/aluno/vagas/${vaga.id}`)} className="card-interactive cursor-pointer">
            <div className="flex items-start gap-3">
              <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
                {vaga.logoUrl ? (
                  <img src={vaga.logoUrl} alt="" className="w-8 h-8 rounded object-contain" />
                ) : (
                  <Building2 className="w-6 h-6 text-primary" />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-2 mb-1">
                  <h3 className="font-medium text-foreground line-clamp-2">{vaga.titulo}</h3>
                  <div className="flex items-center gap-1.5 shrink-0">
                    {vaga.hasSkillData && mySkills.length > 0 && <MatchBadge percentage={vaga.matchPercentage} size="sm" showLabel={false} />}
                    <Badge variant="secondary" className="text-[10px]">{vaga.tipoLabel}</Badge>
                  </div>
                </div>
                <p className="text-sm text-muted-foreground">{vaga.empresa}</p>
                <div className="flex items-center gap-3 mt-2 flex-wrap">
                  <WorkModelBadge model={vaga.work_model} size="sm" />
                  {vaga.local && (
                    <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
                      <MapPin className="w-3 h-3" />{vaga.local}
                    </span>
                  )}
                  <DeadlineBadge deadline={vaga.deadline_date} size="sm" />
                </div>
                {vaga.description_summary && (
                  <p className="text-xs text-muted-foreground line-clamp-2 mt-2">{vaga.description_summary}</p>
                )}
                {vaga.skillsTags.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-2">
                    {vaga.skillsTags.slice(0, 3).map((tag) => (
                      <span key={tag} className="text-[10px] px-1.5 py-0.5 rounded-full bg-primary/10 text-primary font-medium">{tag}</span>
                    ))}
                    {vaga.skillsTags.length > 3 && (
                      <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-muted text-muted-foreground font-medium">+{vaga.skillsTags.length - 3}</span>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}
      </motion.div>

      {vagas.length === 0 && (
        <div className="bg-muted/50 rounded-2xl p-8 text-center">
          <Briefcase className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
          <h3 className="font-heading font-semibold text-foreground mb-2">Nenhuma vaga disponível</h3>
          <p className="text-sm text-muted-foreground mb-4">Melhore seu perfil e faça o aquecimento para aumentar suas chances</p>
          <Button variant="accent" onClick={() => navigate("/aluno/carreira")}>Ir para Carreira</Button>
        </div>
      )}
    </div>
  );
}
